import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Slider } from "./ui/slider";

const fmt = (n) =>
  "₹ " + Number(Math.round(n)).toLocaleString("en-IN");

// ===== Individual calculators =====
function SIPCalc() {
  const [amount, setAmount] = React.useState(10000);
  const [years, setYears] = React.useState(15);
  const [rate, setRate] = React.useState(12);
  const n = years * 12;
  const r = rate / 100 / 12;
  const fv = amount * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
  const inv = amount * n;
  return (
    <CalcShell>
      <Field label="Monthly investment (₹)" v={amount} setV={setAmount} min={500} max={500000} step={500} testid="sip-amount" />
      <Field label="Period (years)" v={years} setV={setYears} min={1} max={40} testid="sip-years" />
      <Field label="Expected return (% p.a.)" v={rate} setV={setRate} min={1} max={25} testid="sip-rate" />
      <Result label="Total Invested" value={fmt(inv)} />
      <Result label="Estimated Returns" value={fmt(fv - inv)} accent />
      <Result label="Future Value" value={fmt(fv)} bold />
    </CalcShell>
  );
}

function LumpsumCalc() {
  const [amount, setAmount] = React.useState(100000);
  const [years, setYears] = React.useState(10);
  const [rate, setRate] = React.useState(12);
  const fv = amount * Math.pow(1 + rate / 100, years);
  return (
    <CalcShell>
      <Field label="Investment amount (₹)" v={amount} setV={setAmount} min={1000} max={10000000} step={1000} testid="lumpsum-amount" />
      <Field label="Period (years)" v={years} setV={setYears} min={1} max={40} testid="lumpsum-years" />
      <Field label="Expected return (% p.a.)" v={rate} setV={setRate} min={1} max={25} testid="lumpsum-rate" />
      <Result label="Estimated Returns" value={fmt(fv - amount)} accent />
      <Result label="Future Value" value={fmt(fv)} bold />
    </CalcShell>
  );
}

function EMICalc() {
  const [p, setP] = React.useState(2500000);
  const [r, setR] = React.useState(8.5);
  const [y, setY] = React.useState(20);
  const monthly = r / 100 / 12;
  const n = y * 12;
  const emi = (p * monthly * Math.pow(1 + monthly, n)) / (Math.pow(1 + monthly, n) - 1);
  const total = emi * n;
  return (
    <CalcShell>
      <Field label="Loan amount (₹)" v={p} setV={setP} min={50000} max={50000000} step={10000} testid="emi-principal" />
      <Field label="Interest rate (% p.a.)" v={r} setV={setR} min={4} max={20} step={0.1} testid="emi-rate" />
      <Field label="Tenure (years)" v={y} setV={setY} min={1} max={30} testid="emi-years" />
      <Result label="Monthly EMI" value={fmt(emi)} bold />
      <Result label="Total Interest" value={fmt(total - p)} accent />
      <Result label="Total Payment" value={fmt(total)} />
    </CalcShell>
  );
}

function RetirementCalc() {
  const [age, setAge] = React.useState(30);
  const [retire, setRetire] = React.useState(60);
  const [exp, setExp] = React.useState(50000);
  const [infl, setInfl] = React.useState(6);
  const [rate, setRate] = React.useState(11);
  const years = retire - age;
  const futureMonthly = exp * Math.pow(1 + infl / 100, years);
  const corpus = futureMonthly * 12 * 25;
  const r = rate / 100 / 12;
  const n = years * 12;
  const sip = corpus / (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
  return (
    <CalcShell>
      <Field label="Current age" v={age} setV={setAge} min={18} max={60} testid="retire-age" />
      <Field label="Retirement age" v={retire} setV={setRetire} min={age + 1} max={75} testid="retire-target" />
      <Field label="Current monthly expense (₹)" v={exp} setV={setExp} min={5000} max={500000} step={1000} testid="retire-expense" />
      <Field label="Inflation (% p.a.)" v={infl} setV={setInfl} min={2} max={12} step={0.5} testid="retire-inflation" />
      <Field label="Expected return (% p.a.)" v={rate} setV={setRate} min={6} max={20} testid="retire-rate" />
      <Result label="Required Corpus" value={fmt(corpus)} bold />
      <Result label="Monthly SIP Needed" value={fmt(sip)} accent />
    </CalcShell>
  );
}

function GoalCalc() {
  const [goal, setGoal] = React.useState(2500000);
  const [years, setYears] = React.useState(7);
  const [rate, setRate] = React.useState(12);
  const r = rate / 100 / 12;
  const n = years * 12;
  const sip = goal / (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
  return (
    <CalcShell>
      <Field label="Goal amount (₹)" v={goal} setV={setGoal} min={50000} max={50000000} step={10000} testid="goal-amount" />
      <Field label="Time to goal (years)" v={years} setV={setYears} min={1} max={30} testid="goal-years" />
      <Field label="Expected return (% p.a.)" v={rate} setV={setRate} min={1} max={20} testid="goal-rate" />
      <Result label="Monthly SIP Needed" value={fmt(sip)} bold />
    </CalcShell>
  );
}

function InflationCalc() {
  const [amt, setAmt] = React.useState(100000);
  const [years, setYears] = React.useState(10);
  const [rate, setRate] = React.useState(6);
  const fv = amt * Math.pow(1 + rate / 100, years);
  return (
    <CalcShell>
      <Field label="Current cost (₹)" v={amt} setV={setAmt} min={1000} max={10000000} step={1000} testid="infl-amt" />
      <Field label="Years from now" v={years} setV={setYears} min={1} max={50} testid="infl-years" />
      <Field label="Inflation (% p.a.)" v={rate} setV={setRate} min={1} max={15} step={0.5} testid="infl-rate" />
      <Result label="Future Cost" value={fmt(fv)} bold />
      <Result label="Erosion in Value" value={fmt(fv - amt)} accent />
    </CalcShell>
  );
}

function BondYieldCalc() {
  const [face, setFace] = React.useState(1000);
  const [price, setPrice] = React.useState(950);
  const [coupon, setCoupon] = React.useState(7);
  const [years, setYears] = React.useState(5);
  const c = (coupon / 100) * face;
  const ytm = ((c + (face - price) / years) / ((face + price) / 2)) * 100;
  return (
    <CalcShell>
      <Field label="Face value (₹)" v={face} setV={setFace} min={100} max={1000000} step={100} testid="bond-face" />
      <Field label="Current price (₹)" v={price} setV={setPrice} min={100} max={1000000} step={10} testid="bond-price" />
      <Field label="Coupon rate (%)" v={coupon} setV={setCoupon} min={1} max={20} step={0.1} testid="bond-coupon" />
      <Field label="Years to maturity" v={years} setV={setYears} min={1} max={40} testid="bond-years" />
      <Result label="Approx Yield to Maturity" value={ytm.toFixed(2) + " %"} bold />
    </CalcShell>
  );
}

function InterestCalc() {
  const [p, setP] = React.useState(100000);
  const [r, setR] = React.useState(8);
  const [y, setY] = React.useState(5);
  const simple = (p * r * y) / 100;
  const compound = p * Math.pow(1 + r / 100, y) - p;
  return (
    <CalcShell>
      <Field label="Principal (₹)" v={p} setV={setP} min={1000} max={10000000} step={1000} testid="int-p" />
      <Field label="Rate (% p.a.)" v={r} setV={setR} min={1} max={20} step={0.1} testid="int-r" />
      <Field label="Period (years)" v={y} setV={setY} min={1} max={40} testid="int-y" />
      <Result label="Simple Interest" value={fmt(simple)} />
      <Result label="Compound Interest" value={fmt(compound)} accent />
    </CalcShell>
  );
}

function FVCalc() {
  const [p, setP] = React.useState(100000);
  const [r, setR] = React.useState(8);
  const [y, setY] = React.useState(10);
  const fv = p * Math.pow(1 + r / 100, y);
  return (
    <CalcShell>
      <Field label="Present value (₹)" v={p} setV={setP} min={1000} max={10000000} step={1000} testid="fv-p" />
      <Field label="Annual rate (%)" v={r} setV={setR} min={1} max={20} step={0.1} testid="fv-r" />
      <Field label="Years" v={y} setV={setY} min={1} max={40} testid="fv-y" />
      <Result label="Future Value" value={fmt(fv)} bold />
    </CalcShell>
  );
}

function RORCalc() {
  const [pv, setPv] = React.useState(100000);
  const [fv, setFv] = React.useState(200000);
  const [y, setY] = React.useState(7);
  const ror = (Math.pow(fv / pv, 1 / y) - 1) * 100;
  return (
    <CalcShell>
      <Field label="Initial investment (₹)" v={pv} setV={setPv} min={1000} max={10000000} step={1000} testid="ror-pv" />
      <Field label="Final value (₹)" v={fv} setV={setFv} min={1000} max={100000000} step={1000} testid="ror-fv" />
      <Field label="Years" v={y} setV={setY} min={1} max={50} testid="ror-y" />
      <Result label="Annualised Return" value={ror.toFixed(2) + " %"} bold />
    </CalcShell>
  );
}

function InsurancePremiumCalc() {
  const [age, setAge] = React.useState(30);
  const [cover, setCover] = React.useState(10000000);
  const [years, setYears] = React.useState(30);
  // simple illustrative formula: 0.04% of cover per year, scaled by age factor
  const annual = (cover * 0.0004) * (1 + (age - 25) * 0.04);
  return (
    <CalcShell>
      <Field label="Age" v={age} setV={setAge} min={18} max={65} testid="ins-age" />
      <Field label="Sum assured (₹)" v={cover} setV={setCover} min={100000} max={100000000} step={100000} testid="ins-cover" />
      <Field label="Policy term (years)" v={years} setV={setYears} min={5} max={40} testid="ins-years" />
      <Result label="Indicative Annual Premium" value={fmt(annual)} bold />
      <p className="text-xs text-muted-foreground col-span-full">Indicative only — actual premium depends on insurer underwriting, riders and health profile.</p>
    </CalcShell>
  );
}

function SumInsuredCalc() {
  const [income, setIncome] = React.useState(1200000);
  const [liab, setLiab] = React.useState(2500000);
  const recommended = income * 12 + liab;
  return (
    <CalcShell>
      <Field label="Annual income (₹)" v={income} setV={setIncome} min={100000} max={50000000} step={10000} testid="suminsured-income" />
      <Field label="Outstanding liabilities (₹)" v={liab} setV={setLiab} min={0} max={100000000} step={10000} testid="suminsured-liab" />
      <Result label="Recommended Cover (12× income + liabilities)" value={fmt(recommended)} bold />
    </CalcShell>
  );
}

function RiskProfiler() {
  const [answers, setAnswers] = React.useState({});
  const questions = [
    { id: "age", q: "Your age group?", opts: [["< 30", 4], ["30 – 45", 3], ["45 – 60", 2], ["60+", 1]] },
    { id: "horizon", q: "Investment horizon?", opts: [["< 3 years", 1], ["3 – 5 years", 2], ["5 – 10 years", 3], ["> 10 years", 4]] },
    { id: "drop", q: "If your portfolio dropped 20% in a year you would…", opts: [["Sell everything", 1], ["Sell some", 2], ["Hold", 3], ["Buy more", 4]] },
    { id: "exp", q: "Investment experience?", opts: [["None", 1], ["Some MF/FD", 2], ["MF + Equity", 3], ["MF + Equity + Derivatives/AIF", 4]] },
    { id: "goal", q: "Primary goal?", opts: [["Capital preservation", 1], ["Steady income", 2], ["Long-term growth", 3], ["Aggressive growth", 4]] },
  ];
  const score = Object.values(answers).reduce((a, b) => a + b, 0);
  const max = questions.length * 4;
  const pct = (score / max) * 100;
  let profile = "—";
  if (Object.keys(answers).length === questions.length) {
    if (pct < 40) profile = "Conservative";
    else if (pct < 65) profile = "Moderate";
    else if (pct < 85) profile = "Growth";
    else profile = "Aggressive";
  }
  return (
    <div className="space-y-6">
      {questions.map((q, i) => (
        <div key={q.id}>
          <p className="font-medium mb-3">{i + 1}. {q.q}</p>
          <RadioGroup value={String(answers[q.id] || "")} onValueChange={(v) => setAnswers({ ...answers, [q.id]: Number(v) })}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {q.opts.map(([label, val]) => (
                <Label key={label} className="flex items-center gap-2 border border-border rounded-md p-3 cursor-pointer hover:bg-accent">
                  <RadioGroupItem value={String(val)} data-testid={`risk-${q.id}-${val}`} />
                  <span>{label}</span>
                </Label>
              ))}
            </div>
          </RadioGroup>
        </div>
      ))}
      {profile !== "—" && (
        <div className="rounded-lg bg-accent text-accent-foreground p-5">
          <div className="text-xs uppercase tracking-widest">Your Risk Profile</div>
          <div className="font-serif text-3xl mt-1">{profile}</div>
          <div className="text-sm mt-1">Score: {score} / {max}</div>
        </div>
      )}
    </div>
  );
}

// ===== Helpers =====
function Field({ label, v, setV, min, max, step = 1, testid }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <Input type="number" value={v} onChange={(e) => setV(Number(e.target.value) || 0)} min={min} max={max} step={step} data-testid={testid} className="mt-1" />
      <Slider value={[v]} onValueChange={(val) => setV(val[0])} min={min} max={max} step={step} className="mt-3" />
    </div>
  );
}

function Result({ label, value, accent, bold }) {
  return (
    <div className={`rounded-md p-4 ${accent ? "bg-accent text-accent-foreground" : "bg-muted"} ${bold ? "ring-2 ring-primary" : ""}`}>
      <div className="text-xs uppercase tracking-widest opacity-80">{label}</div>
      <div className={`font-serif ${bold ? "text-2xl" : "text-xl"} mt-1`}>{value}</div>
    </div>
  );
}

function CalcShell({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>;
}

// Map calculator key → component + display name
const REG = {
  sip:        { name: "SIP Calculator", C: SIPCalc },
  lumpsum:    { name: "Lumpsum Calculator", C: LumpsumCalc },
  emi:        { name: "EMI Calculator", C: EMICalc },
  retirement: { name: "Retirement Calculator", C: RetirementCalc },
  goal:       { name: "Goal Planner", C: GoalCalc },
  inflation:  { name: "Inflation Impact", C: InflationCalc },
  bond:       { name: "Bond Yield Calculator", C: BondYieldCalc },
  interest:   { name: "Interest Calculator", C: InterestCalc },
  fv:         { name: "Future Value Calculator", C: FVCalc },
  ror:        { name: "Rate of Return Calculator", C: RORCalc },
  insurance:  { name: "Insurance Premium", C: InsurancePremiumCalc },
  suminsured: { name: "Sum Insured Calculator", C: SumInsuredCalc },
  risk:       { name: "Risk Profiler", C: RiskProfiler },
};

export default function Calculators({ keys }) {
  const list = keys ?? Object.keys(REG);
  const [active, setActive] = React.useState(list[0]);
  const ActiveCalc = REG[active]?.C;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-serif">{REG[active]?.name}</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={active} onValueChange={setActive}>
          <TabsList className="flex flex-wrap h-auto justify-start gap-1">
            {list.map((k) => (
              <TabsTrigger key={k} value={k} data-testid={`calc-tab-${k}`} className="text-xs">
                {REG[k]?.name}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={active} className="mt-6">
            {ActiveCalc ? <ActiveCalc /> : null}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
